import { useMutation, useQueryClient } from '@tanstack/react-query';

import API from '@api/API';

import { ProfileContact, ProfileLink } from '@queries/EntertainerQueries';
import {
  CurrentPromoter,
  useCurrentPromoterQuery,
  usePromoterManageQuery,
} from '@queries/PromoterQueries';
import { useUserTalentQuery } from '@queries/UserQueries';

interface CreatePromoterMutationPayload {
  avatar?: any;
  title: string;
  description: string;
  cityIds: string[];
  entertainers?: {
    [entertainerId: string]: { roleTitle?: string; weight?: number; role?: string };
  };
}

export interface CreatePromoterMutationResponseBody {
  promoter?: CurrentPromoter;
}

export const useCreatePromoterMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: async ({ avatar, ...payload }: CreatePromoterMutationPayload) => {
      const response = await API.post<
        CreatePromoterMutationResponseBody,
        CreatePromoterMutationPayload
      >('/v1/promoter/me', payload);

      try {
        if (response.promoter && avatar) {
          await API.put<{}, Partial<UpdatePromoterMutationPayload>>(
            `/v1/user/promoter/${response.promoter._id}`,
            {
              avatar,
            },
          );
        }
      } catch (e) {
        console.error(e);
      }

      return response.promoter;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useCurrentPromoterQuery.queryKey() });
    },
  });

  return {
    isSubmitting,
    createPromoterAsync: mutateAsync,
  };
};

interface UpdatePromoterMutationPayload {
  avatar?: any;
  title: string;
  description: string;
  cityIds: string[];
  bookingHandle: string;
  contact: Partial<ProfileContact>;
  linkCopiedAt?: Date;
  entertainers?: {
    [entertainerId: string]: { roleTitle?: string; weight?: number; role?: string };
  };
}

export const useUpdatePromoterMutation = () => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: async ({
      promoterId,
      avatar,
      ...payload
    }: Partial<UpdatePromoterMutationPayload> & { promoterId: string }) => {
      const response = await API.put<{}, Partial<UpdatePromoterMutationPayload>>(
        `/v1/user/promoter/${promoterId}`,
        payload,
      );

      try {
        if (avatar) {
          await API.put<{}, Partial<UpdatePromoterMutationPayload>>(
            `/v1/user/promoter/${promoterId}`,
            {
              avatar,
            },
          );
        }
      } catch (e) {
        console.error(e);
      }

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useCurrentPromoterQuery.queryKey() });
    },
  });

  return {
    isSubmitting,
    updatePromoterAsync: mutateAsync,
  };
};

export interface CreatePromoterEventMutationPayload {
  eventImage: any;
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  cityId: string;
  bookingOpportunityId?: string;
  isRecurring: boolean;
  recurringSchedule?: {
    type: 'weekly' | 'bi-weekly' | 'monthlyNthDay';
  };
  venue: {
    _id?: string;
    title: string;
  };
}

export const useCreatePromoterEventMutation = (promoterId: string) => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: CreatePromoterEventMutationPayload) => {
      return API.post<{}, CreatePromoterEventMutationPayload>(
        `/v1/user/promoter/${promoterId}/events`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usePromoterManageQuery.queryKey(promoterId) });
    },
  });

  return {
    isSubmitting,
    createPromoterEventAsync: mutateAsync,
  };
};

export interface CreateBookingOpportunityMutationPayload {
  title: string;
  details: string;
  startDate: string;
  startTime: string;
  cityId: string;
  venue: {
    _id?: string;
    title: string;
  };
}

export const useCreateBookingOpportunityMutation = (promoterId: string) => {
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: CreateBookingOpportunityMutationPayload) => {
      return API.post<{}, CreateBookingOpportunityMutationPayload>(
        `/v1/user/promoter/${promoterId}/booking-opportunities`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usePromoterManageQuery.queryKey(promoterId) });
    },
  });

  return {
    isSubmitting,
    createBookingOpportunityAsync: mutateAsync,
  };
};

export interface CreateNoAuthBookingOpportunityMutationPayload {
  title: string;
  details: string;
  startDate: string;
  startTime: string;
  cityId: string;
  venue: {
    _id?: string;
    title: string;
  };
  contact: {
    name: string;
    email: string;
    phone: string;
    organization: {
      type: string;
      title: string;
    };
  };
}

export const useNoAuthCreateBookingOpportunityMutation = () => {
  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: CreateNoAuthBookingOpportunityMutationPayload) => {
      return API.post<{}, CreateNoAuthBookingOpportunityMutationPayload>(
        `/v1/booking-opportunities`,
        payload,
      );
    },
  });

  return {
    isSubmitting,
    createNoAuthBookingOpportunityAsync: mutateAsync,
  };
};

const useInvalidateBookingOpportunityQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidateBookingOpportunityQueries: (promoterId?: string) => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: useUserTalentQuery.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: usePromoterManageQuery.queryKey(promoterId),
        }),
      ]);
    },
  };
};

export interface UpdatePromoterBookingOpportunityEntertainerMutationPayload {
  entertainers: { [entertainerId: string]: { status: 'confirmed' | 'rejected' } };
}

export const useUpdatePromoterBookingOpportunityEntertainerMutation = (promoterId: string) => {
  const { invalidateBookingOpportunityQueries } = useInvalidateBookingOpportunityQueries();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({
      bookingOpportunityId,
      entertainerId,
      status,
    }: {
      bookingOpportunityId: string;
      entertainerId: string;
      status: 'confirmed' | 'rejected';
    }) => {
      return API.post<{}, UpdatePromoterBookingOpportunityEntertainerMutationPayload>(
        `/v1/user/booking-opportunities/${bookingOpportunityId}/entertainers`,
        { entertainers: { [entertainerId]: { status } } },
      );
    },
    onSuccess: () => {
      invalidateBookingOpportunityQueries(promoterId);
    },
  });

  return {
    isSubmitting,
    updatePromoterBookingOpportunityEntertainerAsync: mutateAsync,
  };
};

export interface AddPromoterBookingOpportunityUpdateMutationPayload {
  message: string;
}

export const useAddPromoterBookingOpportunityUpdateMutation = (promoterId: string) => {
  const { invalidateBookingOpportunityQueries } = useInvalidateBookingOpportunityQueries();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({
      bookingOpportunityId,
      message,
    }: AddPromoterBookingOpportunityUpdateMutationPayload & {
      bookingOpportunityId: string;
    }) => {
      return API.post<{}, AddPromoterBookingOpportunityUpdateMutationPayload>(
        `/v1/user/booking-opportunities/${bookingOpportunityId}/add-update`,
        { message },
      );
    },
    onSuccess: () => {
      invalidateBookingOpportunityQueries(promoterId);
    },
  });

  return {
    isSubmitting,
    addPromoterBookingOpportunityUpdateAsync: mutateAsync,
  };
};

export interface ConfirmPromoterBookingOpportunityEntertainerMutationPayload {}

export const useConfirmPromoterBookingOpportunityEntertainerMutation = (promoterId: string) => {
  const { invalidateBookingOpportunityQueries } = useInvalidateBookingOpportunityQueries();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ bookingOpportunityId }: { bookingOpportunityId: string }) => {
      return API.post<{}, ConfirmPromoterBookingOpportunityEntertainerMutationPayload>(
        `/v1/user/booking-opportunities/${bookingOpportunityId}/confirm`,
        {},
      );
    },
    onSuccess: () => {
      invalidateBookingOpportunityQueries(promoterId);
    },
  });

  return {
    isConfirmingPromoterBookingOpportunityEntertainers: isPending,
    confirmPromoterBookingOpportunityEntertainersAsync: mutateAsync,
  };
};
